import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  LabeledInput,
  LabeledSelect,
  LabeledTextarea,
} from "@/Components/Form";
import { Inertia } from "@inertiajs/inertia";

import {
  ChevronDown,
  X,
  RefreshCw,
  XCircle,
  Plus,
  MapPin,
} from "react-feather";
import { differenceWith, isEqual, forOwn } from "lodash";

import { FactorGas } from "@/Components/EmissionFactors/FactorGas";
import { formatDate, showToast } from "@/Utilities/Utilities";
import { usePage } from "@inertiajs/inertia-react";
import { FACTOR_STATES } from "@/Enums/factor_states";
import { GAS_OPTIONS } from "@/Enums/gas_options";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import { Formheader, GrayBox } from "@/Components";
import { DefinitionOfScopes } from "@/Components/DefinitionOfScopes";
import { FactorGasCO2e } from "@/Components/EmissionFactors/FactorGasCO2e";
import { toast } from "react-toastify";
import { LabeledPlacesAutocomplete } from "@/Components/PlacesAutocomplete";
import { Info } from "@/Components/Info";
import { EMISSION_FACTOR_DATASETS } from "@/Enums/emission_factor_datasets";
import { EMISSION_FACTOR_DATATYPES } from "@/Enums/emission_factor_datatypes";
import { IconVerifiedBadge } from "@/Icons";
import EmissionFactorDialogCenter from "@/Pages/Factors/Partials/EmissionFactorDialogCenter";

let gasIndex = 0;

export function FactorSettings({
  factor = {
    id: null,
    name: "",
    unit: "",
    year: "",
    description: "",
    source: "",
    standard: "",
    datatype: "",
    scientific_uncertainty: null,
    regional_restrictions: "",
    dataset: "",
    verified: false,
    verified_state: "not_verified",
  },
  saveButton,
  onSave,
  errors,
}) {
  const revisionsPerPage = 5;
  const user = usePage().props?.auth?.user?.data || {};
  const agency = usePage().props?.agency?.data || null;
  const isSuper = user?.is_super || false;
  const isAgency = usePage().props?.organization?.data?.is_agency || false;
  const isEditable =
    !(factor?.global && !isSuper) || !(factor?.global && isAgency);
  // const getFactorState = (v) => FACTOR_STATES.find((e) => e.value == v)?.label || v;
  const getGasOption = (v) => GAS_OPTIONS.find((e) => e.value == v)?.label || v;
  const getScopeOption = (v) =>
    SCOPE_OPTIONS.find((e) => e.value == v)?.label || v;

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const addGasButton = useRef();
  const initialLoad = useRef(true);

  const [pagination, setPagination] = useState({
    page: 1,
    items: [],
    pages: 1,
    start: 0,
    stop: 1,
  });

  const [changelogVisible, setChangelogVisible] = useState(false);
  const [forceFormupdate, setForceFormUpdate] = useState(false);

  const [gases, setGases] = useState(
    factor?.gases
      ?.filter((g) => !g?.parent_emission_factor_gas_id)
      ?.map((g, i) => {
        const childGases = factor?.gases?.filter(
          (cg) => cg?.parent_emission_factor_gas_id === g?.id,
        );

        // Using reduce to accumulate child gas properties into a single object
        const childGasProps = childGases?.reduce(
          (acc, child) => ({
            ...acc,
            [`${child.gas}_factor`]: child.factor,
          }),
          {},
        );

        return {
          ...g,
          index: i,
          ...childGasProps, // Flattened child gas properties
        };
      }) || [],
  );
  const [formData, setFormData] = useState({
    id: factor?.id || null,
    name: factor?.name || "",
    unit: factor?.unit || "",
    verified: factor?.verified || false,
    verified_state: factor?.verified_state || "not_verified",
    year: factor?.year || "",
    description: factor?.description || "",
    source: factor?.source || "",
    standard: factor?.standard || "GHG",
    datatype: factor?.datatype || "",
    scientific_uncertainty:
      factor?.scientific_uncertainty !== null
        ? factor?.scientific_uncertainty
        : false,
    regional_restrictions: factor?.regional_restrictions || "",
    dataset: factor?.dataset || "",
    comment: "",
    verified_3rd_party_documentation:
      factor?.verified_3rd_party_documentation || "",
    verified_3rd_party_email: factor?.verified_3rd_party_email || "",
  });

  const revisionKeys = [
    // keys to show in revision table
    "name",
    "unit",
    "gases",
    // "year",
    // "source",
    // "standard",
    // "datatype",
    // "scientific_uncertainty",
    // "regional_restrictions",
    // "verified_state",
  ];
  const updateKeys = [
    // keys on factor to be checked for updates
    "name",
    "unit",
    "year",
    "description",
    "source",
    "dataset",
    "datatype",
    "scientific_uncertainty",
    "regional_restrictions",
    "verified_state",
    // 'gases',
    // 'verified_state',
  ];

  if (formData.verified_state === "verified_3rd_party") {
    updateKeys.push("verified_3rd_party_documentation");
    updateKeys.push("verified_3rd_party_email");
  }

  // Sets initial factors for form
  useEffect(() => {
    if (initialLoad.current === true) {
      initialLoad.current = false;
      gasIndex = gases.length;
      updatePagination(1);
      if (gasIndex === 0) {
        addGas();
      }
    }
  });

  function isUpdated() {
    if (forceFormupdate) {
      return true;
    }

    for (const key of updateKeys) {
      // If the types of data in the form and on the factor is comparable but not equal the form is updated.
      if (formData[key]?.toString() !== factor?.[key]?.toString()) return true;
    }
    return false;
  }

  function handleSave(e) {
    if (!isUpdated()) return;

    setForceFormUpdate(false);
    saveForm();
  }

  function saveForm() {
    let payload = { ...formData };

    payload.gases = gases;

    // get child gases that has a parent_emission_factor_gas_id and update that gas factor with the value of [gas]_factor from its parent
    const childGases = gases.filter((g) => g?.parent_emission_factor_gas_id);

    childGases.forEach((child) => {
      const parent = gases.find(
        (g) => g.id === child.parent_emission_factor_gas_id,
      );
      child.factor = parent[`${child.gas}_factor`];
    });

    const id = toast.loading("Please wait...");

    // If we have a factor update it, otherwise we create it
    if (factor.id) {
      Inertia.put(`/factors/update/${factor.id}`, payload, {
        preserveScroll: true,
        onSuccess: (r) => {
          if (typeof onSave === "function") onSave(payload);
          updatePagination(pagination.page);
          toast.update(id, {
            render: "The emission factor was successfully updated",
            type: "success",
            isLoading: false,
            autoClose: 5000,
          });
        },
        onError: () => {
          toast.update(id, {
            render: "Something went wrong when updating your emission factor",
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
        },
      });
    } else {
      Inertia.post(`/factors/create`, payload, {
        preserveScroll: true,
        onSuccess: (r) => {
          if (typeof onSave === "function") onSave(payload);
          updatePagination(pagination.page);
          toast.update(id, {
            render: "The emission factor was successfully created",
            type: "success",
            isLoading: false,
            autoClose: 5000,
          });
        },
        onError: () => {
          toast.update(id, {
            render: "Something went wrong when creating your emission factor",
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
        },
      });
    }
  }

  function handleFormUpdate(e) {
    if (!isEditable) {
      return;
    }
    const updateKey = e.target.dataset?.factor || e.target.name;

    setFormData((currentState) => {
      const newstate = { ...currentState };
      newstate[updateKey] =
        e.target?.type === "number"
          ? parseFloat(e.target.value)
          : e.target?.type === "checkbox"
            ? e.target.checked
            : e.target.value;
      return newstate;
    });
  }

  function handleAddLocation({ description, place_id }) {
    setFormData((prevValues) => ({
      ...prevValues,
      regional_restrictions: description,
      place_id: place_id,
    }));
  }

  function addGas() {
    setForceFormUpdate(true);
    setGases((currentState) => {
      return [
        ...currentState,
        {
          index: gasIndex++,
          id: null,
          emission_factor_id: factor.id,
          factor: "",
          gas: false,
          scope: false,
        },
      ];
    });
  }

  function handleGasUpdate(index, data, id) {
    setForceFormUpdate(true);
    setGases((currentState) => {
      const d = [...currentState];
      d[index][data.name] = data.value;
      return d;
    });
  }

  function removeGas(index) {
    setForceFormUpdate(true);
    setGases((currentState) =>
      currentState.filter((item, i) => {
        return i !== index;
      }),
    );
  }

  let lastPrintedRev = null;
  function factorRevision(rev, i) {
    let changelog = [];
    if (lastPrintedRev === null) {
      if (pagination.page == 1)
        lastPrintedRev = factor; // The "first" revision is the current factor
      else lastPrintedRev = factor.revisions[pagination.start - 1];
    }
    if (lastPrintedRev) {
      revisionKeys.forEach((key) => {
        // custom check for gases differences
        if (key === "gases") {
          const old_gases = lastPrintedRev[key].map((gas) => {
            return { gas: gas.gas, factor: gas.factor, scope: gas.scope };
          });
          const new_gases = rev[key].map((gas) => {
            return { gas: gas.gas, factor: gas.factor, scope: gas.scope };
          });

          const diff_added = differenceWith(old_gases, new_gases, isEqual);
          const diff_removed = differenceWith(new_gases, old_gases, isEqual);

          for (const gas of diff_added) {
            changelog.push({
              name: "gas",
              from: null,
              to: `greenhouse gas: ${getGasOption(gas.gas)}, value: ${
                gas.factor
              }, scope: ${getScopeOption(gas.scope)}`,
            });
          }

          for (const gas of diff_removed) {
            changelog.push({
              name: "gas",
              from: `greenhouse gas: ${getGasOption(gas.gas)}, value: ${
                gas.factor
              }, scope: ${getScopeOption(gas.scope)}`,
              to: null,
            });
          }
        } else if (typeof rev[key] === "object" && rev[key] !== null) {
          forOwn(rev[key], function (value, innerKey) {
            if (!isEqual(rev[key][innerKey], lastPrintedRev[key][innerKey])) {
              changelog.push({
                name: getGasOption(key),
                from: rev[key],
                to: lastPrintedRev[key],
              });
            }
          });
        } else if (lastPrintedRev[key] != rev[key]) {
          changelog.push({
            name: getGasOption(key),
            from: rev[key],
            to: lastPrintedRev[key],
          });
        }
      });
    }

    lastPrintedRev = rev;
    return (
      <div key={i} className={`mb-2 flex flex-col rounded-sm py-4`}>
        <div className="border-b pb-2">
          <div className="flex flex-col gap-2">
            <p className="font-bold">
              <span className="mb-1">{rev.comment || "No comment"}</span>
            </p>
            {changelog.map((change, ii) => {
              if (change.name === "verified") {
                return change.to === "verified" ? (
                  <span key={ii} className="mb-1 font-bold">
                    The emission factor has been approved.
                  </span>
                ) : change.to === "denied_verification" ? (
                  <span key={ii} className="mb-1 font-bold">
                    The factor does not pass the requirements.
                  </span>
                ) : (
                  <span key={ii} className="mb-1 font-bold">
                    The factor verification status has been set to{" "}
                    {`${change.to}`}.
                  </span>
                );
              }
              if (change.from === null) {
                return <span key={ii}>{`Added: ${change.to}`}</span>;
              }
              if (change.to === null) {
                return <span key={ii}>{`Removed: ${change.from}`}</span>;
              }
              return (
                <span
                  key={ii}
                >{`${change.name} changed from ${change.from} to ${change.to}`}</span>
              );
            })}
            <div>
              <span className="mr-2 text-sm">
                {formatDate(rev.created_at, "d MMM yyyy HH:mm")}
              </span>
              {rev.user && (
                <span className="text-sm text-typo-inactive">
                  {rev.user.fullname}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function revisionPagination() {
    if (pagination.items.length < 1) return;

    let buttons = [];
    for (let i = 1; i <= pagination.pages; i++) {
      buttons.push({
        label: i,
      });
    }

    return (
      <div className="mt-4 flex flex-col">
        {pagination.items.map((r, i) => factorRevision(r, i))}
        <div className="my-4 flex flex-row justify-center">
          {pagination.pages > 1 && (
            <button
              key="previous"
              onClick={() => updatePagination(pagination.page - 1)}
              className={`px-3 py-2 text-blue underline hover:no-underline disabled:text-primary-disabled disabled:no-underline`}
              disabled={pagination.page < 2}
            >
              Previous
            </button>
          )}
          {buttons.map((b, ii) => (
            <button
              key={ii}
              onClick={() => updatePagination(b.label)}
              className={`mx-1 rounded-sm px-4 py-1 hover:bg-primary-light ${
                b.label == pagination.page ? "bg-primary text-white" : ""
              }`}
            >
              {b.label}
            </button>
          ))}
          {pagination.pages > 1 && (
            <button
              key="next"
              onClick={() => updatePagination(pagination.page + 1)}
              className={`px-3 py-2 text-blue underline hover:no-underline disabled:text-primary-disabled disabled:no-underline`}
              disabled={pagination.page > pagination.pages - 1}
            >
              Next
            </button>
          )}
        </div>
      </div>
    );
  }

  function updatePagination(current) {
    lastPrintedRev = null;
    if (!factor?.revisions || factor?.revisions.length < 1) return;
    const totalPages = Math.ceil(factor.revisions.length / revisionsPerPage);
    const start = (current - 1) * revisionsPerPage;
    const end = Math.min(
      start + revisionsPerPage - 1,
      factor.revisions.length - 1,
    );

    setPagination({
      page: current,
      items: factor.revisions.slice(start, end + 1),
      pages: totalPages,
      start: start,
      end: end,
    });
  }

  function InfoBanner({
    icon,
    text,
    link = null,
    linkText = null,
    closeable = true,
    children,
  }) {
    const [isClosed, setIsClosed] = useState(false);
    const bannerRef = useRef();

    return (
      <div
        className={`shadow-small mb-4 flex w-full max-w-full flex-row justify-between bg-typo px-6 py-6 text-white hover:shadow-hover md:px-12 ${
          isClosed && "hidden"
        }`}
        ref={bannerRef}
      >
        <div className="flex flex-row items-center">
          {icon}
          <span>{text}</span>
        </div>
        <div className="flex flex-col-reverse items-center justify-center md:flex-row">
          {linkText && link && (
            <a
              className="cursor-pointer underline hover:no-underline"
              target="_BLANK"
              href={link}
            >
              {linkText}
            </a>
          )}
          {children}
          {closeable && (
            <button
              className="md:ml-4"
              onClick={() => {
                setIsClosed(true);
              }}
            >
              <XCircle size="24" />
            </button>
          )}
        </div>
      </div>
    );
  }

  const setVerificationStatus = (verified = true) => {
    const id = toast.loading("Please wait...");

    Inertia.put(
      route("factors.update.verification", factor.id),
      {
        verified_state: verified ? "verified" : "denied_verification",
      },
      {
        onSuccess: () => {
          showToast(
            id,
            `The emission factor was ${verified ? "verified" : "denied"}`,
          );
          setFormData((prev) => ({
            ...prev,
            verified_state: verified ? "verified" : "denied_verification",
          }));
        },
        onError: () => {
          showToast(
            id,
            `Something went wrong when ${verified ? "verifying" : "denying"} the emission factor`,
            "error",
          );
        },
      },
    );
  };

  return (
    <>
      {factor.verified_state === "requested" && !isSuper && !isAgency && (
        <InfoBanner
          icon={
            <img
              className="mr-2 inline"
              src={"/images/arrow_red.svg"}
              alt="Approved by Gidas"
              width="20"
            />
          }
          text={`Your emission factor is awaiting verification by ${agency?.name || "Gidås"}`}
          link="/validation-terms"
          linkText="Read terms"
        />
      )}

      {factor.verified_state === "requested" && (isSuper || isAgency) && (
        <InfoBanner
          closeable={false}
          icon={
            <img
              className="mr-2 inline"
              src={"/images/arrow_red.svg"}
              alt="Approved by Gidas"
              width="20"
            />
          }
          text={`${factor.organization.name} has sent an request for this emission factor to be verified`}
        >
          <div className="flex gap-4">
            <button
              className="text-white underline hover:no-underline"
              onClick={() => setVerificationStatus(false)}
            >
              Decline
            </button>
            <button
              className="btn-yellow grow-0"
              onClick={setVerificationStatus}
            >
              Accept
            </button>
          </div>
        </InfoBanner>
      )}

      {factor.verified_state === "verified" && !isSuper && !isAgency && (
        <InfoBanner
          icon={
            agency ? (
              <IconVerifiedBadge
                className="mr-2 inline"
                title={`Approved by ${agency.name}`}
              />
            ) : (
              <img
                className="mr-2 inline"
                src={"/images/gidas_badge.svg"}
                alt="Approved by Gidas"
                width="20"
              />
            )
          }
          text={`Your emission factor is now Verified by ${agency?.name || "Gidås"}!`}
          link="/validation-terms"
          linkText="Read terms"
        />
      )}

      <GrayBox margin="mb-4">
        <div className="flex flex-col gap-4">
          <Formheader margin="">Emission factor details</Formheader>
          <LabeledInput
            required
            disabled={!isEditable}
            name="name"
            value={formData.name}
            title="Heading"
            placeholder="Add heading"
            onChange={handleFormUpdate}
            error={errors?.name}
          />

          <LabeledTextarea
            required
            disabled={!isEditable}
            name="description"
            title="General description*"
            help="Scope, assumptions, conversion factors that are important to consider"
            placeholder="Add your general description"
            onChange={handleFormUpdate}
            value={formData?.description}
            error={errors?.description}
          />

          <div className="flex flex-wrap gap-4">
            <LabeledInput
              required
              disabled={!isEditable}
              name="source"
              title="Source*"
              placeholder="Add source"
              containerClass="w-full sm:basis-1/2-gap-4"
              onChange={handleFormUpdate}
              value={formData?.source}
              error={errors?.source}
            />
            <LabeledInput
              required
              disabled={!isEditable}
              name="year"
              title="Publication year*"
              placeholder="Add year"
              containerClass="w-full sm:basis-1/2-gap-4"
              onChange={handleFormUpdate}
              value={formData?.year}
              error={errors?.year}
            />

            <LabeledSelect
              required
              disabled={!isEditable}
              name="standard"
              title="Standard*"
              containerClass="w-full sm:basis-1/2-gap-4"
              value={"GHG"}
              options={[{ label: "GHG", value: "ghg" }]}
              noDefault={true}
              onChange={handleFormUpdate}
              error={errors?.standard}
            />

            <LabeledInput
              required
              disabled={!isEditable}
              name="unit"
              title="Emission factor unit*"
              placeholder="Add unit"
              containerClass="w-full sm:basis-1/2-gap-4"
              onChange={handleFormUpdate}
              value={formData?.unit}
              error={errors?.unit}
            />

            <LabeledSelect
              disabled={!isEditable}
              name="dataset"
              title="Type of dataset"
              placeholder="Add dataset"
              containerClass="w-full sm:basis-1/2-gap-4"
              onChange={handleFormUpdate}
              options={Object.values(EMISSION_FACTOR_DATASETS)}
              value={formData?.dataset}
              error={errors?.dataset}
            />

            <div className="relative w-full sm:basis-1/2-gap-4">
              <LabeledPlacesAutocomplete
                disabled={!isEditable}
                name="regional_restrictions"
                title="Regional restrictions"
                placeholder="Add restrictions"
                className="w-full pl-10"
                requestOptions={{
                  types: ["(regions)"],
                }}
                defaultValue={formData?.regional_restrictions}
                error={errors?.regional_restrictions}
                onChange={handleAddLocation}
              />
              <MapPin size={18} className="absolute left-3 -mt-8" />
            </div>
          </div>
        </div>
      </GrayBox>

      <GrayBox margin="mb-4">
        <Formheader margin="mb-4">Calculation</Formheader>

        <div className="mt-6 flex w-full flex-col">
          {gases.length > 0 &&
            gases.map((f, i) => {
              if (f.gas === "co2e") {
                return (
                  <FactorGasCO2e
                    index={i}
                    key={f.index}
                    gas={f}
                    unit={formData.unit}
                    onRemove={removeGas}
                    onFormUpdate={handleGasUpdate}
                    isDisabled={!isEditable}
                    errors={errors}
                  />
                );
              }

              return (
                <FactorGas
                  index={i}
                  key={f.index}
                  gas={f}
                  unit={formData.unit}
                  onRemove={removeGas}
                  onFormUpdate={handleGasUpdate}
                  isDisabled={!isEditable}
                  errors={errors}
                />
              );
            })}
        </div>

        {isEditable && (
          <button
            className="flex-grow-0 justify-self-end px-1 py-4 text-blue underline hover:no-underline"
            onClick={addGas}
            ref={addGasButton}
          >
            <Plus size={18} className="inline-block" /> Add impact category or
            green house gas
          </button>
        )}

        <DefinitionOfScopes />
      </GrayBox>

      <GrayBox margin="mb-4">
        <Formheader margin="mb-4">Data quality and variations</Formheader>

        <div className="flex flex-wrap gap-4">
          <LabeledSelect
            required
            disabled={!isEditable}
            name="datatype"
            title="Datatype*"
            containerClass="w-full sm:basis-1/2-gap-4"
            options={Object.values(EMISSION_FACTOR_DATATYPES)}
            defaultOption={"Select datatype"}
            defaultValue={formData?.datatype}
            onChange={handleFormUpdate}
            error={errors?.datatype}
          />

          <LabeledSelect
            required
            disabled={!isEditable}
            name="scientific_uncertainty"
            title={<ScientificUncertaintyLabel />}
            containerClass="w-full sm:basis-1/2-gap-4"
            options={Array.from({ length: 7 }, (_, i) => ({
              label: `${i === 6 ? "> " : ""}${i * 5}%`,
              value: i * 5,
            }))}
            onChange={handleFormUpdate}
            defaultValue={formData?.scientific_uncertainty}
            error={errors?.scientific_uncertainty}
          />

          {!isSuper && !isAgency && (
            <>
              <div className="w-full sm:basis-1/2-gap-4">
                <LabeledSelect
                  disabled={!isEditable || factor?.verified}
                  name="verified_state"
                  title="Verification*"
                  containerClass="w-full"
                  options={
                    factor?.verified
                      ? [
                          {
                            label: `Verified by ${agency?.name || "Gidås"}`,
                            value: "verified",
                          },
                        ]
                      : [
                          {
                            label: "Not verified",
                            value: "not_verified",
                          },
                          {
                            label: "Verified by Third party",
                            value: "verified_3rd_party",
                          },
                          {
                            label: agency
                              ? `I want my Emission Factor verified by ${agency.name}`
                              : "I want my Emission Factor verified by Gidås - 2 500 SEK",
                            value: "requested",
                          },
                        ]
                  }
                  noDefault={true}
                  onChange={handleFormUpdate}
                  defaultValue={formData?.verified_state || "not_verified"}
                  error={errors?.verified_state}
                />
              </div>
              {formData?.verified_state === "requested" && !agency && (
                <div className="mt-4 flex w-full items-start gap-4 bg-primary-light p-4">
                  <img
                    className="mt-2 inline md:ml-4"
                    src={"/images/gidas_badge.svg"}
                    alt={"Verification badge"}
                    width="24"
                  />
                  <div>
                    <p className="mb-2 font-bold">
                      This will add 2500 SEK to your invoice
                    </p>
                    <p className="max-w-fit">
                      By choosing to add Emission Factor Validation I agree to
                      let Gidås review the emission factor to assure
                      methodological quality and calculation reliability
                      according to&nbsp;
                      <a
                        className="underline hover:no-underline"
                        href="/validation-terms"
                        target="_BLANK"
                      >
                        Emission Factor Validation Service Terms
                      </a>
                    </p>
                  </div>
                </div>
              )}
              {formData?.verified_state === "verified_3rd_party" && (
                <div className="flex w-full flex-wrap gap-4">
                  <LabeledInput
                    required
                    disabled={!isEditable}
                    containerClass="w-full sm:basis-1/2-gap-4"
                    name="verified_3rd_party_documentation"
                    value={formData?.verified_3rd_party_documentation || ""}
                    title="Third party verification documentation*"
                    placeholder="Add link to documentation"
                    onChange={handleFormUpdate}
                    error={errors?.verified_3rd_party_documentation}
                  />

                  <LabeledInput
                    required
                    disabled={!isEditable}
                    containerClass="w-full sm:basis-1/2-gap-4"
                    name="verified_3rd_party_email"
                    type="email"
                    value={formData?.verified_3rd_party_email || ""}
                    title="Third party verification email*"
                    placeholder="Add third party email"
                    onChange={handleFormUpdate}
                    error={errors?.verified_3rd_party_email}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </GrayBox>

      {isUpdated() && factor.id !== null && (
        <GrayBox margin="mb-4">
          <Formheader margin="mb-4">Add comment for update</Formheader>
          <Input
            name="comment"
            placeholder="Add comment or calculation method"
            className="bg-slate-50 px-4"
            onChange={handleFormUpdate}
          />
        </GrayBox>
      )}

      {factor?.updated_at && factor?.revisions?.length > 0 && (
        <GrayBox margin="mb-4">
          <div className="flex flex-col rounded-sm border px-4">
            <div
              className="relative mb-4 mt-4 flex cursor-pointer flex-row justify-between"
              onClick={() =>
                factor?.revisions?.length > 0
                  ? setChangelogVisible(!changelogVisible)
                  : null
              }
            >
              <div className="flex flex-row items-center justify-center">
                <RefreshCw />
                <span className="ml-2 mr-4 text-md font-bold">Edits</span>
                {/* <span className="text-slate">Last edited: {formatDate(factor?.updated_at)}</span> */}
              </div>
              {factor?.revisions?.length > 0 && (
                <ChevronDown
                  className={`absolute right-0 top-1/2 -translate-y-1/2 transform py-4 transition-all ${
                    changelogVisible ? "rotate-180" : ""
                  }`}
                  size="58"
                />
              )}
            </div>
            {changelogVisible && revisionPagination()}
          </div>
        </GrayBox>
      )}

      {isEditable && (
        <div className="flex content-end justify-between">
          <div className="grow-1 flex">
            {(isSuper || isAgency) && factor.verified_state !== null && (
              <LabeledSelect
                name="verified_state"
                data-key="verified_state"
                title="Verification status"
                value={formData?.verified_state || false}
                options={FACTOR_STATES}
                onChange={handleFormUpdate}
                className="h-14 w-48"
              />
            )}
          </div>
          {!(factor?.global && !isSuper && !isAgency) && (
            <div className="flex items-end justify-center">
              {factor?.id && (
                <button
                  className="ml-2 flex-grow-0 justify-self-end px-1 py-2 text-[#F00000] underline hover:no-underline"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete emission factor
                </button>
              )}
              <button
                className="btn ml-4 flex-grow-0 justify-self-end"
                disabled={formData.id !== null && !isUpdated()}
                onClick={handleSave}
              >
                {saveButton || "Save changes"}
              </button>
            </div>
          )}
        </div>
      )}
      <EmissionFactorDialogCenter
        factor={factor}
        setShowDeleteModal={setShowDeleteModal}
        showDeleteModal={showDeleteModal}
      />
    </>
  );
}

function ScientificUncertaintyLabel() {
  return (
    <div className="flex gap-2">
      Scientific uncertainty*
      <Info>
        <p>
          Scientific uncertainty refers to the uncertainty associated with
          quantifying the parameters used as data inputs (e.g., activity data,
          emission factors, or other parameters) to calculation models.
          Parameter uncertainties can be evaluated through statistical analysis,
          precision determinations of measurement equipment, and expert
          assessments. Quantifying parameter uncertainties and then estimating
          source category uncertainties based on these parameter uncertainties
          should be the primary focus for companies choosing to investigate the
          uncertainty in their emission inventories according to the GHG
          Protocol. With the scientific setup you can estimate uncertainty
          expressed as a percentage for this emission factor model. A high
          percentage reflect high uncertainty, and a low percentage reflects
          better precision. For all inventories in this report, the Emma
          platform quantifies uncertainty in a statistical model.
        </p>
      </Info>
    </div>
  );
}
